
import React from 'react';
import { Name } from "./name";
import { sprintf } from 'sprintf-js';
import { TEXT_FONT_SIZE } from "../flow-render";
import { deep_copy } from "../js-utils";
import { get_div_bbox } from "./bbox_utils";

class Text {

  static type = "Text";

  /**
   * @param name: Name instance
   * @param initValue: optional initial value
   * @param fake: See lib/fake.js.
   *   {
   *     path: {str} path to the fake function
   *     arg0: {?} optional
   *     arg1: {?} optional
   *   }
   */
  constructor(name, initValue, fake) {
    if (!(name instanceof Name)) {
      throw String("Text constructor argument must be Name");
    }
    this.name = name;
    this.type = Text.type;
    if (initValue) {
      this.initValue = initValue;
    }
    if (fake) {
      this.fake = fake;
    }
    this.ref = React.createRef();

    this.setValidator(str => str);
    this.setBlurValidator(str => {
      return str;
    });
  }

  /**
   * Initialize the component state. Usually called from
   * the component constructor.
   */
  initState = (state, initValue) => {
    state[this.name.id] = this.defaultObj();
    if (initValue) {
      this.setValue(state, initValue);
    } else if (this.initValue) {
      this.setValue(state, this.initValue);
    }
  };

  /**
   * Subclasses override this to provide a custom default state.
   */
  defaultObj() {
    return {
      value: "",
      disable: false,
      type: Text.type,
      highlight: false,
    };
  }

  /**
   * Subclasses override this to convert the {@param value}
   * representation to the display string.
   */
  valueToString(value) {
    return value;
  }

  /**
   * Initialize the {@param componentState} from the
   * {@param appState}.
   */
  loadFromState = (appState, componentState) => {
    if (!appState || appState[this.name.id] === undefined) {
      return;
    }

    if (!componentState[this.name.id]) {
      componentState[this.name.id] = this.defaultObj();
    }

    this.setValue(componentState, appState[this.name.id]);
  };

  /**
   * Returns an object to be stored in the app state.
   */
  saveToState = state => {
    const result = {};
    if (this.getValue(state)) {
      result[this.name.id] = this.getValue(state);
    }
    return result;
  };

  /**
   * Initialize {@param state} with {@param value}.
   */
  setValue(state, value) {
    state[this.name.id].value = value;
  };

  copyState = (state) => {
    return {
      [this.name.id]: {
        ...state[this.name.id]
      }
    }
  };

  getValue(state) {
    if (this.name.id in state) {
      return state[this.name.id].value;
    }
    return "";
  }

  /**
   * Returns true if the parameter is unfilled.
   *
   * @param state {Object}
   * @return {boolean}
   */
  isBlank = state => {
    if (!this.getValue(state)) {
      return true;
    }
    return this.getValue(state).trim() === '';
  };

  /**
   * If the parameter is filled, return true else add the highlght rect.
   *
   * @param state {Object}
   * @param highlight_rects {[Object]} rects
   */
  isFilledElseHighlight = (state, highlight_rects) => {
    if (!this.ref.current) {
      // Reference is not yet initialized, which means the text box was not yet shown
      return true;
    }
    const blank = this.isBlank(state);
    if (blank) {
      const bbox = get_div_bbox(this.ref);
      if (bbox) {
        highlight_rects.push(bbox);
      } else {
        console.log("Null BBOX at");
        console.trace();
      }
    }
    return !blank;
  };

  getHighlightRect = () => {
    return get_div_bbox(this.ref);
  }

  getAppValue = appState => {
    return appState[this.name.id];
  }

  onChange = component => event => {
    let id = event.target.id;
    let newState = deep_copy(component.state);
    newState[id].value = this.validator(event.target.value);
    newState.dirty = true;

    if (this.onChangeCallback) {
      this.onChangeCallback(component, newState, this);
    }

    component.setState(newState);
  };

  onFocus = (component, hasFocus) => event => {
    component.hasFocus = hasFocus;
    if (!hasFocus) {
      this.onChange(component)(event);
      if (this.onAction) {
        this.onAction(component, this);
      }
    }
    const orig_value = this.getValue(component.state);
    const new_value = removeXMLTags(this.blurValidator(orig_value));
    if (new_value !== orig_value) {
      component.setState({
        [this.name.id]: {
          value: new_value,
        }
      })
    }

  }

  onKeyDown = component => event => {
    const ENTER_KEY = 13;
    const TAB_KEY = 9;
    if (event.keyCode === ENTER_KEY) {
      this.ref.current.blur();
    }
    if (event.keyCode === ENTER_KEY || event.keyCode === TAB_KEY) {
      if (this.onAction) {
        this.onAction(component, this);
      }
      const orig_value = this.getValue(component.state);
      const new_value = removeXMLTags(this.blurValidator(orig_value));
      if (new_value !== orig_value) {
        component.setState({
          [this.name.id]: {
            value: new_value,
          }
        })
      }
    }
  };

  setDisabled = (state, disable) => {
    let id = this.name.id;
    state[id].disable = disable;
  };

  setHighlight = (state, highlight) => {
    state[this.name.id].highlight = highlight;
  };

  getHighlight = state => {
    return state[this.name.id].highlight;
  };

  getDisabled = state => {
    if (!(this.name.id in state)) {
      return false;
    }
    return state[this.name.id].disable;
  };

  renderInputDiv = (component, style_override = {}, super_text) => {
    const super_style = {
      fontFamily: "Poppins",
      fontSize: "0.9rem",
    }
    return (
      <div>
        { super_text && <div><span style={super_style}>{super_text}</span></div> }
        { this.renderInput(component, style_override) }
      </div>
    )
  };

  renderInput = (component, style_override={}, error) => {
    const bg_style = {};
    if (this.getDisabled(component.state)) {
      bg_style.backgroundColor = "#CCC";
    }
    const style = {
      fontFamily: "Poppins",
      fontSize: "1.1rem",
      padding: "0.2rem",
      ...bg_style,
      ...style_override,
    };
    const extra_props = {};
    if (this.hint) {
      extra_props.placeholder = this.hint;
    }
    let value = "";
    let disabled = "";
    if (this.name.id in component.state) {
      value = component.state[this.name.id].value;
      if (component.state[this.name.id].disable) {
        disabled = "disabled";
      }
    }

    return (
        <React.Fragment>
            <input
                type="text"
                style={style}
                value={value}
                id={this.name.id}
                ref={this.ref}
                disabled={disabled}
                onChange={this.onChange(component)}
                onFocus={this.onFocus(component, true)}
                onKeyDown={this.onKeyDown(component)}
                onBlur={this.onFocus(component, false)}
                onInput={this.onChange(component)}
                { ...extra_props }
            />
            {/* {error } */}
        </React.Fragment>
      
    );
  };

  render = (component, inputStyle) => {

    let style = {
      marginLeft: "1rem",
      fontSize: TEXT_FONT_SIZE,
      ...inputStyle,
    };
    return (
      <span>
        { this.name.label }
        { this.renderInput(component, style) }
      </span>);
  }

  setValidator = validator => {
    this.validator = validator;
  }

  setBlurValidator = validator => {
    this.blurValidator = validator;
  }
}

const renderMulti = (prmList, component) => {
  let items = [];

  prmList.forEach(prm => {
    items.push( (
      <div className="column" key={prm.name.id}>
        <p>{prm.name.label}</p>
        <p>{ prm.renderInput(component) }</p>
      </div>
    ));
  });

  return (
    <div className="columns">
      { items }
    </div>
  );
};

const filter = (text, valid_chars) => {
  let result = "";
  for(let i=0; i < text.length; i++) {
    if (valid_chars.includes(text[i])) {
      result += text[i];
    }
  }
  return result;
};

const phoneValidator = orig_string => {
  const validChars = " 0123456789+()-";
  return filter(orig_string, validChars);
};

const insert_charcters = (str, char, positions, total_str_len) => {
  let result = "";
  for(let i=0; i < str.length ; i++) {
    if (is_digit(str[i])) {
      if (positions.includes(i)) {
        result += char;
      }
      result += str[i];
    } else if (str[i] === char) {
      if (positions.includes(i)) {
        result += char;
      }
    }
  }
  return result.substring(0, total_str_len);
}

const insert_characters_reverse = (str, char, positions) => {
  let result = "";
  for(let i=str.length - 1; i >= 0; i--) {
    const rPos = i- str.length;
    if (is_digit(str[i])) {
      if (positions.includes(rPos)) {
        result = char + result;
      }
      result = str[i] + result;
    } else if (str[i] === char) {
      if (positions.includes(rPos)) {
        result += char;
      }
    }
  }
  return result;
}

const commaValidator = orig_string => {
  const validChars = "0123456789";
  return insert_characters_reverse(
    filter(orig_string, validChars),
    ",", [-4, -7, -10, -13, -16, -19]);
}

const dobValidator = orig_string => {
  const validChars = "0123456789/";
  return insert_charcters(
    filter(orig_string, validChars),
    "/", [2, 5], 10);
};

const int_clamp = (val_str, max_val) => {
  let val = parseInt(val_str, 10);
  val = Math.min(max_val, val);
  return sprintf("%02d", val);
}

const mmddyyyyValidator = orig_string => {
  const items = orig_string.split("/");
  if (items.length !== 3) {
    console.log("MMDDYYYY error", orig_string);
    return orig_string;
  }
  let year = items[2];
  if (year.length === 4) {
    return orig_string;
  }
  year = parseInt(year, 10);
  if (year < 30) {
    year = 2000 + year;
  } else {
    year = 1900 + year;
  }
  return int_clamp(items[0], 12) + "/"
    + int_clamp(items[1], 31) + "/" + year.toString();
}

const isOver18Validator = orig_string => {
  const dateFormatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

  // return false if the date string does not match "MM/DD/YYYY" format
  if (!dateFormatRegex.test(orig_string)) {
    return false;
  }

  const today = new Date();
  const birthDate = new Date(orig_string);

  // Check for invalid date or future date
  if (isNaN(birthDate.getTime()) || birthDate >= today) {
    return false;
  }

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age >= 18;
}

const is_digit = str => {
  const digitRE = /^\d$/;
  return digitRE.test(str);
};

const ssnValidator = orig_string => {
  const validChar = "0123456789-";
  return insert_charcters(
    filter(orig_string, validChar),
    "-", [3, 6], 11);
};

const digitsValidator = orig_string => {
  const validChars = "0123456789";
  return filter(orig_string, validChars);
};

const dollarValidator = orig_string => {
  const validChars = "0123456789,.";
  return filter(orig_string, validChars);
};

const floatValidator = orig_string => {
  const validChars = "0123456789.+-";
  return filter(orig_string, validChars);
}

const removeXMLTags = orig_string => {
  return orig_string.replace( /(<([^>]+)>)/ig, '');
}


export { Text, renderMulti, phoneValidator, dobValidator,
  ssnValidator, digitsValidator, dollarValidator, mmddyyyyValidator,
  floatValidator, commaValidator, isOver18Validator };
